import {
  enumFinancialAccountType,
  enumFinancialAccountSortOption,
  FinancialAccountSortOption,
  FinancialAccountType,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { mutationFinancialAccountCreateAtom } from '../_graphql/mutationFinancialAccountCreate';
import { mutationFinancialAccountSyncAtom } from '../_graphql/mutationFinancialAccountSync';
import { mutationFinancialAccountSyncTransactionsAtom } from '../_graphql/mutationFinancialAccountSyncTransactions';
import { mutationFinancialAccountUpdateAtom } from '../_graphql/mutationFinancialAccountUpdate';
import { financialAccountsQueryAtom } from '../_graphql/queryFinancialAccounts';
import { FinancialAccountRecord } from '../types';

import { atomFromQueryString } from '~/shared/state';

const accountTypeFilterOptions: {
  key: string;
  value: FinancialAccountType[];
}[] = [
  {
    key: 'all',
    value: [],
  },
  ...Object.values(enumFinancialAccountType).map((type) => ({
    key: type,
    value: [type],
  })),
];

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $selectedType = atomFromQueryString.string<string>('t');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);
export const $currencies = atomFromQueryString.string<string>('c');
export const $visibleInPayments = atomFromQueryString.boolean('v');

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedAccounts = atom<Set<string>>(new Set<string>());
export const $isEditDialogOpen = atom<boolean>(false);
export const $editingAccount = atom<FinancialAccountRecord | undefined>(
  undefined,
);
export const $transactionAccount = atom<FinancialAccountRecord | undefined>(
  undefined,
);
export const $isTransactionDrawerOpen = atom<boolean>(false);
export const $sorting = atom<FinancialAccountSortOption[]>([
  enumFinancialAccountSortOption.CreatedAtDesc,
]);

// 🟢🟢🟢 COMPUTED DATA -> real accountTypes array by selected accountTypes filter
export const $accountTypes = atom((get) => {
  const selectedTypes = get($selectedType);

  if (selectedTypes === 'all' || !selectedTypes) return [];

  return (
    accountTypeFilterOptions.find((option) => option.key === selectedTypes)
      ?.value ?? []
  );
});

// 🟢🟢🟢 REMOTE DATA
export const $remoteFinancialAccounts = financialAccountsQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const accountTypes = get($accountTypes);
  const currencies = get($currencies);
  const visibleInPayments = get($visibleInPayments);
  const limit = get($limit);
  const offset = get($offset);
  const sorting = get($sorting);

  const params: {
    searchTerm?: string;
    types?: FinancialAccountType[];
    currencies?: string[];
    visibleInPayments?: boolean;
    limit?: number;
    offset?: number;
    sorting: FinancialAccountSortOption[];
  } = {
    limit,
    offset,
    sorting,
  };

  if (searchTerm) params.searchTerm = searchTerm;
  if (accountTypes && accountTypes.length > 0) params.types = accountTypes;
  if (currencies) params.currencies = currencies.split(',').filter(Boolean);
  if (visibleInPayments !== undefined)
    params.visibleInPayments = visibleInPayments;

  return {
    variables: params,
  };
});

// 🟠🟠🟠 ACTION -> CREATE ACCOUNT
export const $createFinancialAccount = mutationFinancialAccountCreateAtom({});

// 🟠🟠🟠 ACTION -> UPDATE ACCOUNT
export const $updateFinancialAccount = mutationFinancialAccountUpdateAtom({});

// 🟠🟠🟠 ACTION -> SYNC ACCOUNT
export const $syncFinancialAccount = mutationFinancialAccountSyncAtom({});

// 🟠🟠🟠 ACTION -> SYNC ACCOUNT TRANSACTIONS
export const $syncAccountTransactions =
  mutationFinancialAccountSyncTransactionsAtom({});
